import React, { useEffect, useState } from "react";
import {
  BUSINESS_TYPES,
  MODIFIER_FLAGS,
} from "../lib/businessTypeConfig";

/**
 * Admin-side editor for the business adaptivity profile.
 *
 * Reads / writes:
 *   customer.company_parameters.business_profile = {
 *     primary_business_model:  "manufacturing" | ... | null,
 *     additional_models:       string[],
 *     modifier_flags:          { imports, exports, has_physical_retail, b2b, b2c },
 *   }
 *
 * The actual save goes through the existing
 *   PATCH /api/admin/company-parameters
 * endpoint — no new backend route, no new schema.
 *
 * The component is "controlled" — the parent owns the value and the save
 * handler. We render the form, validate, and call onChange() to bubble the
 * complete profile object back up.
 */

const containerStyle = { padding: "16px 24px", borderTop: "1px solid #F3F4F6" };
const headerRow      = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 };

function Pill({ active, populated, children, onClick, testid }) {
  // Stubs render in a lighter shade so the admin sees what's populated.
  const baseBg     = active ? "#1E3A5F" : (populated ? "#EFF6FF" : "#F3F4F6");
  const baseColor  = active ? "white"   : (populated ? "#1E3A5F" : "#6B7280");
  const baseBorder = active ? "#1E3A5F" : (populated ? "#BFDBFE" : "#D1D5DB");
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        background: baseBg, color: baseColor, border: `1px solid ${baseBorder}`,
        borderRadius: 999, padding: "6px 12px", fontSize: 12, fontWeight: 600,
        cursor: "pointer",
      }}
    >
      {children}
      {!populated && (
        <span style={{
          background: active ? "#FACC15" : "#FEF3C7",
          color:      active ? "#1E3A5F" : "#92400E",
          fontSize: 9.5, fontWeight: 700, padding: "1px 5px", borderRadius: 4,
          textTransform: "uppercase", letterSpacing: 0.4,
        }}>
          Stub
        </span>
      )}
    </button>
  );
}

function Toggle({ active, onClick, testid, label, blurb }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      style={{
        display: "flex", alignItems: "flex-start", gap: 10,
        background: active ? "#ECFDF5" : "#F9FAFB",
        border: `1px solid ${active ? "#86EFAC" : "#E5E7EB"}`,
        borderRadius: 8, padding: "10px 12px", width: "100%",
        cursor: "pointer", textAlign: "left",
      }}
    >
      <span style={{
        width: 14, height: 14, borderRadius: 3, marginTop: 2,
        background: active ? "#10B981" : "white",
        border: `2px solid ${active ? "#059669" : "#9CA3AF"}`,
      }} />
      <span>
        <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#0F1B2D" }}>{label}</span>
        <span style={{ display: "block", fontSize: 11.5, color: "#6B7280", marginTop: 2 }}>{blurb}</span>
      </span>
    </button>
  );
}

export default function BusinessProfilePanel({ value, onChange, disabled }) {
  // Local state mirrors `value` so the inputs feel responsive while still
  // bubbling each change up to the parent for persistence.
  const [profile, setProfile] = useState(() => normalise(value));

  useEffect(() => { setProfile(normalise(value)); }, [JSON.stringify(value)]);

  const update = (next) => {
    setProfile(next);
    onChange?.(next);
  };

  const setPrimary = (typeId) => {
    update({
      ...profile,
      primary_business_model: typeId,
      // If the new primary was previously in additional_models, drop it from there.
      additional_models: profile.additional_models.filter((id) => id !== typeId),
    });
  };

  const toggleAdditional = (typeId) => {
    if (typeId === profile.primary_business_model) return;
    const set = new Set(profile.additional_models);
    if (set.has(typeId)) set.delete(typeId);
    else set.add(typeId);
    update({ ...profile, additional_models: Array.from(set) });
  };

  const toggleFlag = (flagId) => {
    update({
      ...profile,
      modifier_flags: {
        ...profile.modifier_flags,
        [flagId]: !profile.modifier_flags[flagId],
      },
    });
  };

  return (
    <div data-testid="business-profile-panel" style={containerStyle}>
      <div style={headerRow}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0F1B2D" }}>
            Business Profile — adapt the dashboard to this company
          </div>
          <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>
            Pick a primary business model + any additional models the customer also runs.
            Modifier flags layer extra panels on top.
          </div>
        </div>
      </div>

      {/* PRIMARY business model */}
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>
          Primary business model
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {Object.values(BUSINESS_TYPES).map((t) => {
            const active = profile.primary_business_model === t.id;
            return (
              <Pill
                key={t.id}
                active={active}
                populated={t.populated}
                onClick={() => disabled ? null : setPrimary(t.id)}
                testid={`bp-primary-${t.id}`}
              >
                {t.label}
              </Pill>
            );
          })}
        </div>
      </div>

      {/* ADDITIONAL business models */}
      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>
          Additional business models (optional)
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {Object.values(BUSINESS_TYPES).map((t) => {
            const isPrimary = profile.primary_business_model === t.id;
            const active    = profile.additional_models.includes(t.id);
            return (
              <Pill
                key={t.id}
                active={active}
                populated={t.populated}
                onClick={() => disabled ? null : toggleAdditional(t.id)}
                testid={`bp-additional-${t.id}`}
              >
                {t.label}{isPrimary ? " (primary)" : ""}
              </Pill>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: "#6B7280", marginTop: 6 }}>
          Tap a model to add it as a secondary group. The primary is excluded automatically.
        </div>
      </div>

      {/* MODIFIER flags */}
      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>
          Modifier flags
        </div>
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8,
        }}>
          {Object.values(MODIFIER_FLAGS).map((f) => (
            <Toggle
              key={f.id}
              active={!!profile.modifier_flags[f.id]}
              onClick={() => disabled ? null : toggleFlag(f.id)}
              testid={`bp-flag-${f.id}`}
              label={f.label}
              blurb={f.blurb}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function normalise(v) {
  return {
    primary_business_model: v?.primary_business_model || null,
    additional_models:      Array.isArray(v?.additional_models) ? v.additional_models : [],
    modifier_flags: {
      imports:             !!v?.modifier_flags?.imports,
      exports:             !!v?.modifier_flags?.exports,
      has_physical_retail: !!v?.modifier_flags?.has_physical_retail,
      b2b:                 !!v?.modifier_flags?.b2b,
      b2c:                 !!v?.modifier_flags?.b2c,
    },
  };
}
